import {
	GetQueryId,
	OrderProps,
	OrderListProps,
	Lineitem,
} from '../../interfaces/Ordel.Interface';
import { CategoryStatus } from '../../interfaces/OrderStatus.Interface';
import type { AnalysisLineItem } from '../../pages/Orders/AddInternalOrder/AnalysisStep';
import {
	CamDataJawsRow,
	CamDataStockRow,
	Fuson360SetupDataExport,
} from '../../interfaces/Fusion360SetupData.interfaces';
import { api } from '../slices/api';

// One row per machine_model returned by the `cam-data/` GET endpoint. The
// backend uses DISTINCT ON to keep the most recent version per machine_model
// and serializes each export with the same shape as the legacy
// `fusion_360_cam_data_exports` field (nested machine / machine_model /
// setup_<n>_jaw_assembly_configuration objects), so consumers can keep using
// `Fuson360SetupDataExport`.
export type CamDataExportRow = Fuson360SetupDataExport;

export interface ResponseMutation {
	isSuccess: string;
	url: string;
	data: {
		id: string;
	};
}

export interface UpdateLineItem {
	id: string;
	values:
		| {
				material?: string;
				material_request?: string;
				partsbadger_production_method?: string;
				is_keyence_program_required?: boolean;
				is_additional_tooling_required?: boolean;
				requires_inspection_sample_labeling?: boolean;
				ballooning_status?: string;
				aql_table?: string | null;
				run_difficulty?: number | null;
				machine_setup_time_minutes?: number | null;
				revenue?: number | null;
				handling_spec?: string | null;
				cleaning_spec?: string | null;
		  }
		| FormData;
}

export interface OrderQuery {
	page?: number;
	search?: string;
	status?: string;
	production_method?: string;
	building?: string;
	ordering?: string;
	page_size?: number;
	order_type?: 'internal' | 'external';
}

interface IuseGetOrderCountsQuery {
	received_count: number;
	in_progress_count: number;
	completed_count: number;
}

// Define a service using a base URL and expected endpoints
export const extendedApiSlice = api.injectEndpoints({
	endpoints: (builder) => ({
		getOrders: builder.query<
			{ count: number; results: OrderListProps[] },
			OrderQuery
		>({
			query: (params) => {
				const queryParams = new URLSearchParams();
				Object.entries(params).forEach(([key, value]) => {
					if (value !== undefined) {
						queryParams.append(key, value.toString());
					}
				});
				return `orders/?${queryParams.toString()}`;
			},
		}),
		getOrderById: builder.query<OrderProps, GetQueryId>({
			query: ({ id }: GetQueryId) => `orders/${id}/`,
			// Scoped so `finalizeInternalOrder` (below) can invalidate just this
			// order's cache entry instead of a blanket, provider-less 'Order' tag.
			providesTags: (_result, _error, arg) => [{ type: 'Order', id: arg.id }],
		}),
		addOrder: builder.mutation<ResponseMutation, ResponseMutation>({
			query: (values: ResponseMutation) => ({
				url: 'orders/add/',
				method: 'POST',
				body: values,
			}),
		}),
		updateOrderLineItem: builder.mutation<Lineitem, UpdateLineItem>({
			query: ({ id, values }: UpdateLineItem) => ({
				url: 'orders/line-items/' + id + '/',
				method: 'PATCH',
				body: values,
			}),
			invalidatesTags: ['OrderLineItem'],
		}),
		// Order-level flag. Switching it on marks the order's line items too, so
		// an order with many line items needs one click (PTB-6619).
		setOrderInspectionSampleLabeling: builder.mutation<
			{ requires_inspection_sample_labeling: boolean },
			{ id: string; required: boolean }
		>({
			query: ({ id, required }) => ({
				url: `orders/${id}/inspection-sample-labeling/`,
				method: 'PATCH',
				body: { requires_inspection_sample_labeling: required },
			}),
			invalidatesTags: ['OrderLineItem'],
		}),
		// Handling / cleaning for every line item of the order in one click. A
		// dedicated action rather than a field on the order PATCH: it cascades,
		// and a round-tripped order payload should not be able to rewrite twenty
		// line items by accident.
		applyOrderProcesses: builder.mutation<
			Record<string, string>,
			{ id: string; handling_spec?: string; cleaning_spec?: string }
		>({
			query: ({ id, ...body }) => ({
				url: `orders/${id}/apply-processes/`,
				method: 'PATCH',
				body,
			}),
			invalidatesTags: (_result, _error, arg) => [
				'OrderLineItem',
				{ type: 'Order', id: arg.id },
			],
		}),
		getOrderLineItemById: builder.query<Lineitem, GetQueryId>({
			query: ({ id }: GetQueryId) => `orders/line-items/${id}/`,
			providesTags: (result, error, arg) => [{ type: 'OrderLineItem', id: arg.id }],
		}),
		// V2 of the order line item detail endpoint. The payload omits the
		// heavy `fusion_360_cam_data_exports` array; consumers should fetch
		// CAM export data separately via the dedicated `cam-data/*`
		// sub-endpoints so the main page can render without waiting on it.
		getOrderLineItemByIdV2: builder.query<
			Omit<Lineitem, 'fusion_360_cam_data_exports' | 'cam_data_exists'>,
			GetQueryId
		>({
			query: ({ id }: GetQueryId) => `orders/line-items/${id}/v2/`,
			providesTags: (result, error, arg) => [{ type: 'OrderLineItem', id: arg.id }],
		}),
		// Single endpoint that returns the full CAM data for the line item:
		// one row per machine_model export, each carrying the full cam_data
		// JSON and the metadata needed for tab labels / Fusion 360 button.
		getCamData: builder.query<CamDataExportRow[], GetQueryId>({
			query: ({ id }) => `orders/line-items/${id}/cam-data/`,
			providesTags: (_result, _error, arg) => [
				{ type: 'OrderLineItem', id: arg.id },
			],
		}),
		// Jaw Configuration station slice: per machine_model export, the jaw
		// configs (with wireframe + stock fields), setup_count and the setup-2
		// soft jaw overlay — no `cam_data` blob (10–40 MB per export in prod).
		getCamDataJaws: builder.query<CamDataJawsRow[], GetQueryId>({
			query: ({ id }) => `orders/line-items/${id}/cam-data/jaws/`,
			providesTags: (_result, _error, arg) => [
				{ type: 'OrderLineItem', id: arg.id },
			],
		}),
		getCamDataStock: builder.query<CamDataStockRow[], GetQueryId>({
			query: ({ id }) => `orders/line-items/${id}/cam-data/stock/`,
			providesTags: (_result, _error, arg) => [
				{ type: 'OrderLineItem', id: arg.id },
			],
		}),
		getOrderStatusType: builder.query<CategoryStatus[], void>({
			query: () => 'orders/line-item-status-type/',
		}),
		getOrderCounts: builder.query<IuseGetOrderCountsQuery, void>({
			query: () => `orders/order_counts/`,
		}),
		createInternalOrder: builder.mutation<OrderProps, Record<string, unknown>>({
			query: (body) => ({
				url: 'orders/internal/',
				method: 'POST',
				body,
			}),
		}),
		uploadInternalFiles: builder.mutation<
			Record<string, string>,
			{ id: string; formData: FormData }
		>({
			query: ({ id, formData }) => ({
				url: `orders/line-items/${id}/upload-internal-files/`,
				method: 'PATCH',
				body: formData,
			}),
			invalidatesTags: ['OrderLineItem'],
		}),
		// Flips an internal draft order to active. 409 with `{ not_ready: [...] }`
		// when any line item is still missing a file or an analysis result.
		finalizeInternalOrder: builder.mutation<OrderProps, string>({
			query: (orderId) => ({
				url: `orders/${orderId}/finalize/`,
				method: 'POST',
			}),
			// Matches getOrderById's providesTags shape so the order-detail page
			// (InternalDraftBanner's host) refetches on its own once `is_draft`
			// flips — no manual refetch() needed in the banner.
			invalidatesTags: (_result, _error, orderId) => [
				{ type: 'Order', id: orderId },
			],
		}),
		// Re-queues the SFA full analysis for a single draft line item.
		reanalyzeInternalFile: builder.mutation<{ status: string }, string>({
			query: (oliId) => ({
				url: `orders/line-items/${oliId}/reanalyze-internal/`,
				method: 'POST',
			}),
		}),
		// Order-scoped, poll-friendly line item list. Passing `order` makes the
		// backend include draft orders' line items, which the plain list hides.
		getInternalOrderLineItems: builder.query<
			{ results: AnalysisLineItem[] },
			string
		>({
			query: (orderId) => ({
				url: 'orders/line-items/',
				params: { order: orderId, page_size: 100 },
			}),
			providesTags: ['OrderLineItem'],
		}),
	}),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
	useGetOrdersQuery,
	useLazyGetOrdersQuery,
	useGetOrderByIdQuery,
	useLazyGetOrderByIdQuery,
	useAddOrderMutation,
	useUpdateOrderLineItemMutation,
	useSetOrderInspectionSampleLabelingMutation,
	useApplyOrderProcessesMutation,
	useGetOrderLineItemByIdQuery,
	useLazyGetOrderLineItemByIdQuery,
	useGetOrderLineItemByIdV2Query,
	useLazyGetOrderLineItemByIdV2Query,
	useGetCamDataQuery,
	useGetCamDataJawsQuery,
	useGetCamDataStockQuery,
	useLazyGetCamDataJawsQuery,
	useGetOrderStatusTypeQuery,
	useGetOrderCountsQuery,
	useCreateInternalOrderMutation,
	useUploadInternalFilesMutation,
	useFinalizeInternalOrderMutation,
	useReanalyzeInternalFileMutation,
	useGetInternalOrderLineItemsQuery,
} = extendedApiSlice;
